import React, { useState, useEffect, useMemo } from "react";
import { Plus, Pencil, X, FileWarning, Users, User, UserCheck } from "lucide-react";
import { api } from "../../utils/api";

const formatTL = (value) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(value || 0);
const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export default function Charges() {
  const [list, setList] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  
  const today = new Date().toISOString().slice(0, 10);
  const defaults = { scope: "all", target_code: "", target_name: "", amount: "", description: "", date: today, due_date: "" };
  const [form, setForm] = useState(defaults);

  const load = () => {
    setLoading(true);
    api.get("/charges").then(r => {
      setList(r.data || []);
      setLoading(false);
    }).catch(err => {
      console.error("Borçlandırmalar çekilemedi:", err);
      setLoading(false);
    });
  };

  useEffect(() => {
    load();
    api.get("/owners").then(r => {
      setOwners((r.data || []).slice().sort((a, b) => (a.owner_name || "").localeCompare(b.owner_name || "", "tr")));
    }).catch(err => console.error("Malikler çekilemedi:", err));
  }, []);

  const openNew = () => { setForm(defaults); setEditing(null); setOpen(true); };
  
  const openEdit = (row) => { 
    setForm({ ...defaults, ...row, amount: row.amount }); 
    setEditing(row); 
    setOpen(true); 
  };

  const remove = async (row) => {
    if (!window.confirm("Bu borçlandırma kaydını silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/charges/${row.id}`);
      load();
    } catch (err) {
      alert("Silme işlemi başarısız oldu.");
    }
  };

  const change = e => setForm({ ...form, [e.target.name]: e.target.value });
  
  const changeScope = e => setForm({ ...form, scope: e.target.value, target_code: "", target_name: "" });
  
  const chooseTarget = e => {
    const o = owners.find(x => x.accounting_code === e.target.value);
    if (!o) { setForm({ ...form, target_code: "", target_name: "" }); return; }
    setForm({ 
      ...form, 
      target_code: o.accounting_code, 
      target_name: form.scope === "tenant" ? (o.resident_name || "") : (o.owner_name || "") 
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.scope !== "all" && !form.target_code) { alert("Lütfen bir kişi seçin!"); return; }
    
    const payload = { ...form, amount: Number(form.amount) };
    try {
      if (editing) {
        await api.put(`/charges/${editing.id}`, payload);
      } else {
        await api.post("/charges", payload);
      }
      setOpen(false);
      load();
    } catch (x) {
      alert(x.response?.data?.detail || "Kayıt başarısız oldu.");
    }
  };

  const scopeLabel = { all: "Tüm Malikler", owner: "Malik", tenant: "Kiracı" };
  const tenantOwners = useMemo(() => owners.filter(o => o.resident_name && o.resident_name.trim()).slice().sort((a, b) => (a.resident_name || "").localeCompare(b.resident_name || "", "tr")), [owners]);
  const targetList = form.scope === "tenant" ? tenantOwners : owners;
  
  const perHead = Number(form.amount || 0);
  const totalVillas = owners.length || 91;
  const grandTotal = form.scope === "all" ? (perHead * totalVillas) : perHead;

  // Hedef tipine göre şık rozet (badge) oluşturan fonksiyon
  const renderScopeTag = (scope, targetCode, targetName) => {
    if (scope === "all") return <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#e0e7ff", color: "#4f46e5", padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: "bold" }}><Users size={14} /> Tüm Malikler</span>;
    if (scope === "owner") return <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#dcfce7", color: "#16a34a", padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: "bold" }}><UserCheck size={14} /> Malik: {targetName} <small style={{ color: "#15803d", fontWeight: "normal" }}>({targetCode})</small></span>;
    if (scope === "tenant") return <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#ffedd5", color: "#ea580c", padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: "bold" }}><User size={14} /> Kiracı: {targetName} <small style={{ color: "#c2410c", fontWeight: "normal" }}>({targetCode})</small></span>;
    return <span>{scope}</span>;
  };

  return (
    <div style={{ animation: "fadeIn 0.4s ease-out" }}>
      
      {/* ÜST BİLGİ VE BUTON */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", background: "white", padding: "24px 30px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", marginBottom: "24px", gap: "20px" }}>
        <div>
          <span style={{ fontSize: "12px", fontWeight: "bold", color: "#f59e0b", letterSpacing: "1px", textTransform: "uppercase" }}>BORÇLANDIRMA İŞLEMLERİ</span>
          <h2 style={{ margin: "6px 0", fontSize: "22px", color: "#1e293b" }}>Borçlandırma Kayıtları</h2>
          <p style={{ margin: 0, fontSize: "14px", color: "#64748b" }}>Ek ödeme ve özel borçlandırmaları toplu veya kişiye özel olarak oluşturun.</p>
        </div>
        <div>
          <button onClick={openNew} style={{ display: "flex", alignItems: "center", gap: "8px", background: "#3b82f6", color: "white", border: "none", padding: "12px 20px", borderRadius: "8px", fontWeight: "bold", cursor: "pointer", transition: "background 0.2s", boxShadow: "0 2px 10px rgba(59, 130, 246, 0.3)" }}>
            <Plus size={18} /> Yeni Borçlandırma
          </button>
        </div>
      </div>

      {/* LİSTE / TABLO */}
      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>Borçlandırma kayıtları yükleniyor...</div>
      ) : list.length > 0 ? (
        <div style={{ background: "white", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>Tarih</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>Borçlandırılan (Hedef)</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>Açıklama</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>Son Ödeme</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600", textAlign: "right" }}>Tutar</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600", textAlign: "center" }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {list.map(c => (
                  <tr key={c.id} style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.2s" }} onMouseOver={e => e.currentTarget.style.background = '#f8fafc'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: "14px 16px", color: "#64748b", fontSize: "14px", fontWeight: "500" }}>{formatDate(c.date)}</td>
                    <td style={{ padding: "14px 16px" }}>{renderScopeTag(c.scope, c.target_code, c.target_name)}</td>
                    <td style={{ padding: "14px 16px", fontWeight: "600", color: "#334155" }} title={c.description}>
                      {c.description.length > 35 ? c.description.substring(0, 35) + "..." : c.description}
                    </td>
                    <td style={{ padding: "14px 16px", color: "#64748b", fontSize: "14px" }}>{formatDate(c.due_date)}</td>
                    <td style={{ padding: "14px 16px", textAlign: "right", fontWeight: "bold", color: "#ef4444", fontSize: "15px" }}>
                      {formatTL(c.amount)}
                      {c.scope === "all" && <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: "normal", marginTop: "2px" }}>× {totalVillas} hane</div>}
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "center" }}>
                      <div style={{ display: "inline-flex", gap: "6px" }}>
                        <button onClick={() => openEdit(c)} title="Düzenle" style={{ background: "#f1f5f9", color: "#3b82f6", border: "none", padding: "8px", borderRadius: "8px", cursor: "pointer", display: "inline-flex" }}>
                          <Pencil size={16} />
                        </button>
                        <button onClick={() => remove(c)} title="Sil" style={{ background: "#fef2f2", color: "#ef4444", border: "none", padding: "8px", borderRadius: "8px", cursor: "pointer", display: "inline-flex" }}>
                          <X size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div style={{ background: "white", borderRadius: "16px", border: "1px dashed #cbd5e1", padding: "60px 20px", textAlign: "center", color: "#64748b" }}>
          <FileWarning size={48} color="#cbd5e1" style={{ marginBottom: "16px" }} />
          <h3 style={{ margin: "0 0 8px 0", color: "#1e293b" }}>Henüz borçlandırma kaydı yok</h3>
          <p style={{ margin: 0, fontSize: "14px" }}>Yeni Borçlandırma butonu ile toplu veya kişiye özel borç oluşturabilirsiniz.</p>
        </div>
      )}

      {/* YENİ / DÜZENLE MODALI */}
      {open && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "white", width: "100%", maxWidth: "550px", borderRadius: "20px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)", overflow: "hidden", animation: "slideUp 0.3s ease-out" }}>
            
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
              <div>
                <span style={{ fontSize: "12px", fontWeight: "bold", color: "#f59e0b", letterSpacing: "1px" }}>{editing ? "BORÇLANDIRMA DÜZELT" : "YENİ BORÇLANDIRMA"}</span>
                <h2 style={{ margin: "4px 0 0 0", fontSize: "18px", color: "#1e293b" }}>{scopeLabel[form.scope] || "Borçlandırma"}</h2>
              </div>
              <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", padding: "4px", display: "flex" }}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={save} style={{ padding: "24px" }}>
              
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "16px", marginBottom: "16px", background: "#f1f5f9", padding: "16px", borderRadius: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Borçlandırılacak Hedef</label>
                  <select name="scope" value={form.scope} onChange={changeScope} style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", fontSize: "14px", fontFamily: "inherit", background: "white" }}>
                    <option value="all">Tüm Malikler (Toplu Borçlandırma)</option>
                    <option value="owner">Sadece Bir Malik</option>
                    <option value="tenant">Sadece Bir Kiracı</option>
                  </select>
                </div>

                {form.scope !== "all" && (
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: form.scope === "tenant" ? "#ea580c" : "#16a34a", marginBottom: "6px" }}>
                      {form.scope === "tenant" ? "Kiracı Seçin" : "Malik Seçin"}
                    </label>
                    <select name="target_code" value={form.target_code} onChange={chooseTarget} required style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", fontSize: "14px", fontFamily: "inherit", background: "white" }}>
                      <option value="">— Kişi Seçiniz —</option>
                      {targetList.map(o => (
                        <option key={o.accounting_code} value={o.accounting_code}>
                          {form.scope === "tenant" ? (o.resident_name || "(İsimsiz)") : (o.owner_name || "(İsimsiz)")} · {o.villa_no} · {o.accounting_code}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Kişi Başı Tutar (TL)</label>
                  <input name="amount" type="number" min="1" step="0.01" value={form.amount} onChange={change} placeholder="0,00" required style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", fontSize: "14px", fontFamily: "inherit", fontWeight: "bold", color: "#ef4444" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Tahakkuk Tarihi</label>
                  <input name="date" type="date" value={form.date} onChange={change} required style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", fontSize: "14px", fontFamily: "inherit" }} />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                <div style={{ gridColumn: "span 2" }}>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Açıklama (Örn: Kamera sistemi ek ödemesi)</label>
                  <input name="description" value={form.description} onChange={change} required style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", fontSize: "14px", fontFamily: "inherit" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Son Ödeme Tarihi (Opsiyonel)</label>
                  <input name="due_date" type="date" value={form.due_date} onChange={change} style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", fontSize: "14px", fontFamily: "inherit" }} />
                </div>
              </div>

              {/* TOPLU BORÇLANDIRMA UYARISI */}
              {form.scope === "all" && form.amount > 0 && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "13px", color: "#991b1b", fontWeight: "600" }}>BİLANÇOYA EKLENECEK TOPLAM BORÇ:<br/><small>({totalVillas} hane × {formatTL(perHead)})</small></span>
                  <span style={{ fontSize: "20px", color: "#b91c1c", fontWeight: "900" }}>{formatTL(grandTotal)}</span>
                </div>
              )}

              <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: form.scope !== "all" ? "20px" : "0" }}>
                <button type="button" onClick={() => setOpen(false)} style={{ padding: "12px 20px", background: "white", border: "1px solid #cbd5e1", borderRadius: "8px", color: "#475569", fontWeight: "600", cursor: "pointer" }}>
                  Vazgeç
                </button>
                <button type="submit" style={{ padding: "12px 24px", background: "#3b82f6", border: "none", borderRadius: "8px", color: "white", fontWeight: "bold", cursor: "pointer", boxShadow: "0 2px 10px rgba(59, 130, 246, 0.3)" }}>
                  {editing ? "Güncelle" : "İşlemi Kaydet"}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}
    </div>
  );
}